import React from 'react';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Phone, MessageCircle, MapPin, Mail, Clock } from 'lucide-react';

export const companyInfo = {
  name: "UNITYPRO HOMES",
  tagline: "Developers Pvt. Ltd.",
  slogan: "खुशियों का नया address",
  managingDirector: "Vipin Yadav",
  designation: "Managing Director",
  phones: ["9910579770", "9205196614"],
  whatsapp: "9910579770",
  address: "1st Floor, Anil Singh Complex, Near MCD Toll Kapashera, Delhi",
  mapUrl: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d14090.5422185788!2d77.03990788321345!3d28.5590517997266!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390d03f8199cb1a5%3A0x7ab3e6b4696c6a63!2sMCD%20Toll%20Kapashera!5e0!3m2!1sen!2sin!4v1704201546801!5m2!1sen!2sin"
};

const Contact = () => {
  const handleWhatsApp = () => {
    window.open(`https://wa.me/91${companyInfo.whatsapp}?text=Hi, I want to connect with UNITYPRO HOMES`, '_blank');
  };

  const handleCall = (phone) => {
    window.location.href = `tel:+91${phone}`;
  };

  const handleEmail = () => {
    window.location.href = 'mailto:info@unitypro.com';
  };

  return (
    <div className="contact-page min-h-screen bg-black pt-24 pb-20">
      
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-b from-gray-900 to-black">
        <div className="container mx-auto px-4 text-center">
          <Badge className="mb-4 bg-orange-500/20 text-orange-400 border-orange-500/50">
            Get In Touch
          </Badge>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            <span className="gradient-text">Contact Us</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            We're here to help you find your dream property. Reach out to us anytime!
          </p>
        </div>
      </section>

      {/* Contact Cards */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">

            {/* Phone Card */}
            <Card className="contact-method-card p-8 text-center group cursor-pointer" onClick={() => handleCall(companyInfo.phones[0])}>
              <div className="contact-icon-wrapper mb-6">
                <Phone className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-white">Call Us</h3>
              {companyInfo.phones.map((phone, idx) => (
                <p key={idx} className="text-orange-400 font-semibold mb-1">+91 {phone}</p>
              ))}
              <p className="text-gray-500 text-sm mt-3">Mon - Sun: 9AM - 9PM</p>
            </Card>

            {/* WhatsApp Card */}
            <Card className="contact-method-card p-8 text-center group cursor-pointer" onClick={handleWhatsApp}>
              <div className="contact-icon-wrapper mb-6">
                <MessageCircle className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-white">WhatsApp</h3>
              <p className="text-orange-400 font-semibold mb-1">+91 {companyInfo.whatsapp}</p>
              <p className="text-gray-500 text-sm mt-3">Quick Response 24/7</p>
            </Card>

            {/* Email Card */}
            <Card className="contact-method-card p-8 text-center group cursor-pointer" onClick={handleEmail}>
              <div className="contact-icon-wrapper mb-6">
                <Mail className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-white">Email Us</h3>
              <p className="text-orange-400 font-semibold mb-1">info@unitypro.com</p>
              <p className="text-gray-500 text-sm mt-3">We'll reply within 24hrs</p>
            </Card>

            {/* Location Card */}
            <Card className="contact-method-card p-8 text-center group">
              <div className="contact-icon-wrapper mb-6">
                <MapPin className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-white">Visit Us</h3>
              <p className="text-gray-400 text-sm leading-relaxed">{companyInfo.address}</p>
            </Card>
          </div>

          {/* Map and Quick Contact Section */}
          <div className="grid lg:grid-cols-2 gap-8">

            {/* Map */}
            <Card className="map-card overflow-hidden">
              <iframe
                src={companyInfo.mapUrl}
                width="100%"
                height="500"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full h-full"
              ></iframe>
            </Card>

            {/* Quick Contact Info */}
            <Card className="contact-info-section p-10">
              <h2 className="text-3xl font-bold mb-6">
                <span className="gradient-text">Visit Our Office</span>
              </h2>
              
              <div className="space-y-8">
                {/* Address */}
                <div className="flex items-start mb-4">
                  <MapPin className="w-6 h-6 text-orange-400 mr-4 mt-1" />
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">Office Address</h3>
                    <p className="text-gray-400 leading-relaxed">{companyInfo.address}</p>
                  </div>
                </div>

                {/* Office Hours */}
                <div className="flex items-start mb-4">
                  <Clock className="w-6 h-6 text-orange-400 mr-4 mt-1" />
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">Office Hours</h3>
                    <p className="text-gray-400">Monday - Sunday</p>
                    <p className="text-gray-400">9:00 AM - 9:00 PM</p>
                  </div>
                </div>

                {/* Contact Numbers */}
                <div className="flex items-start mb-4">
                  <Phone className="w-6 h-6 text-orange-400 mr-4 mt-1" />
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">Contact Numbers</h3>
                    {companyInfo.phones.map((phone, idx) => (
                      <p key={idx} className="text-orange-400 font-semibold">+91 {phone}</p>
                    ))}
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="pt-6 border-t border-gray-800">
                  <h3 className="text-xl font-semibold text-white mb-4">Quick Actions</h3>
                  <div className="flex gap-3">
                    <Button className="flex-1 cta-button-primary" onClick={handleWhatsApp}>
                      <MessageCircle className="w-4 h-4 mr-2" />
                      WhatsApp
                    </Button>
                    <Button className="flex-1 cta-button-secondary" onClick={() => handleCall(companyInfo.phones[0])}>
                      <Phone className="w-4 h-4 mr-2" />
                      Call Now
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Managing Director Section */}
      <section className="py-16 bg-gradient-to-b from-black to-gray-900">
        <div className="container mx-auto px-4">
          <Card className="md-contact-card p-12 text-center">
            <Badge className="mb-4 bg-orange-500/20 text-orange-400 border-orange-500/50">
              Direct Contact
            </Badge>
            <h2 className="text-4xl font-bold mb-4">
              <span className="gradient-text">Connect with Our Managing Director</span>
            </h2>
            <div className="mb-6">
              <p className="text-2xl font-bold text-white mb-2">{companyInfo.managingDirector}</p>
              <p className="text-orange-400 text-lg">{companyInfo.designation}</p>
            </div>
            <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
              For premium property inquiries and investment consultations, connect directly with our Managing Director
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button size="lg" className="cta-button-primary" onClick={handleWhatsApp}>
                <MessageCircle className="w-5 h-5 mr-2" />
                WhatsApp {companyInfo.managingDirector}
              </Button>
              <Button size="lg" variant="outline" className="cta-button-secondary" onClick={() => handleCall(companyInfo.phones[0])}>
                <Phone className="w-5 h-5 mr-2" />
                Call Directly
              </Button>
            </div>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Contact;
